import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { normalizeRole, type CanonicalRole } from '../permissions';

/**
 * Provides RBAC permission helpers based on the user's current organization role.
 *
 * Role hierarchy (from highest to lowest):
 *   Owner (consultor_master) → Administrador (admin) → Operador (usuario) → Visualizador (viewer)
 *
 * Priority: workspace.userRole (active org) > user.role (auth token)
 */
export function usePermission() {
  const { user } = useAuth();
  const { workspace } = useWorkspace();

  // Prefer the role from the active workspace (org-specific) over the auth user role
  const rawRole = workspace?.userRole ?? user?.role ?? null;
  const canonical: CanonicalRole = normalizeRole(rawRole) ?? 'usuario';

  /** True for Owner (consultor_master) or Administrador (admin) */
  const isAdmin = canonical === 'consultor_master' || canonical === 'admin';

  /** True for Operador only (canonical: usuario) */
  const isOperador = canonical === 'usuario';

  /** True for Visualizador only */
  const isViewer = canonical === 'viewer';

  /** False only for Visualizador — indicates the user may perform write operations */
  const canEdit = canonical !== 'viewer';

  return { isAdmin, isOperador, isViewer, canEdit, role: canonical };
}
